/**
 * CheckpointModal Component (AC-571)
 *
 * Modal for creating a new named checkpoint for a project.
 * Allows users to specify a name (required) and description (optional).
 *
 * Parent Component: HistoryPage
 *
 * API Contract:
 * - createCheckpoint(projectId: number, name: string, description?: string) -> Promise<CheckpointSummary>
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useVersionStore } from '../../store/versionStore';

interface CheckpointModalProps {
  isOpen: boolean;
  projectId: number;
  onClose: () => void;
  onCheckpointCreated: () => void;
}

const CheckpointModal: React.FC<CheckpointModalProps> = ({
  isOpen,
  projectId,
  onClose,
  onCheckpointCreated,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { createCheckpoint } = useVersionStore();

  // Clear form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setNameError(null);
      setSubmitError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    // Clear validation error when user types
    if (nameError) {
      setNameError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name
    if (!name.trim()) {
      setNameError('Name is required');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await createCheckpoint(projectId, name.trim(), description.trim() || undefined);
      onCheckpointCreated();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create checkpoint';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-labelledby="checkpoint-modal-title"
        aria-modal="true"
        className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 id="checkpoint-modal-title" className="text-xl font-semibold text-gray-900 dark:text-warm-white">
            Create Checkpoint
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Name field */}
            <div>
              <label htmlFor="checkpoint-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                id="checkpoint-name"
                type="text"
                value={name}
                onChange={handleNameChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-warm-white ${
                  nameError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="e.g., Before major refactor"
              />
              {nameError && (
                <p className="mt-1 text-sm text-red-600">{nameError}</p>
              )}
            </div>

            {/* Description field */}
            <div>
              <label htmlFor="checkpoint-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="checkpoint-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="Optional description of this checkpoint..."
              />
            </div>

            {/* Error message */}
            {submitError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckpointModal;
